import React, { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  getMaterials,
  deleteMaterial,
  deleteMaterialsBulk,
  updateMaterial,
} from "../../services/apiService";
import MaterialImport from "./MaterialImport";
import MaterialFilterBar from "./MaterialFilterBar";
import { formatCurrency } from "../../utils/formatters";
import { formatPriceAge, isPriceStale } from "../../utils/materialFlow";
import { MoreVertical } from "lucide-react";
import WorkbenchShell from "../workbench/WorkbenchShell";
import { StatusCell } from "../workbench/WorkbenchStatusBar";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import "./adminShell.css";
import "./MaterialAdminPage.css";

function MaterialAdminPage() {
  const navigate = useNavigate();
  const [materials, setMaterials] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedMaterialIds, setSelectedMaterialIds] = useState(new Set());
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [minPrice, setMinPrice] = useState("");
  const [maxPrice, setMaxPrice] = useState("");
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [editError, setEditError] = useState("");
  const [savingMaterialId, setSavingMaterialId] = useState(null);
  // Which row the inspector is describing, and which row's ⋮ menu is open.
  const [focusedMaterialId, setFocusedMaterialId] = useState(null);
  const [rowMenuMaterial, setRowMenuMaterial] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const ignoreBlurRef = useRef(false);

  const fetchMaterials = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await getMaterials();
      setMaterials(data || []);
    } catch (err) {
      console.error("Failed to fetch materials:", err);

      setError(err.message || "Failed to fetch materials");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchMaterials();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);
    return () => clearTimeout(timeout);
  }, [searchTerm]);

  useEffect(() => {
    setSelectedMaterialIds((prev) => {
      const validIds = new Set(materials.map((material) => material.id));
      const next = new Set();
      prev.forEach((id) => {
        if (validIds.has(id)) next.add(id);
      });
      return next;
    });
  }, [materials]);

  const filteredMaterials = useMemo(() => {
    const term = debouncedSearchTerm.trim().toLowerCase();
    const minValue = minPrice === "" ? null : Number(minPrice);
    const maxValue = maxPrice === "" ? null : Number(maxPrice);
    return materials.filter((material) => {
      const matchesName = !term || material.name.toLowerCase().includes(term);
      const cost = Number(material.cost_per_sqft || 0);
      const matchesMin =
        minValue === null || (!Number.isNaN(minValue) && cost >= minValue);
      const matchesMax =
        maxValue === null || (!Number.isNaN(maxValue) && cost <= maxValue);
      return matchesName && matchesMin && matchesMax;
    });
  }, [materials, debouncedSearchTerm, minPrice, maxPrice]);

  const handleAddClick = () => {
    navigate("/admin/materials/new");
  };

  const handleEditMaterial = (material) => {
    navigate(`/admin/materials/${material.id}/edit`);
  };

  const handleDeleteClick = (material) => {
    const shouldUseBulk =
      selectedMaterialIds.size > 0 && selectedMaterialIds.has(material.id);
    if (shouldUseBulk) {
      const selectedTargets = materials.filter((item) =>
        selectedMaterialIds.has(item.id),
      );
      setDeleteTargets(selectedTargets);
    } else {
      setDeleteTargets([material]);
    }
    setShowDeleteConfirm(true);
  };

  const handleCancelDelete = () => {
    setDeleteTargets([]);
    setShowDeleteConfirm(false);
  };

  const handleConfirmDelete = async () => {
    if (deleteTargets.length === 0) return;

    setIsDeleting(true);
    setError(null);

    try {
      if (deleteTargets.length === 1) {
        await deleteMaterial(deleteTargets[0].id);
      } else {
        await deleteMaterialsBulk(deleteTargets.map((material) => material.id));
      }
      setShowDeleteConfirm(false);
      setDeleteTargets([]);
      setSelectedMaterialIds((prev) => {
        const next = new Set(prev);
        deleteTargets.forEach((material) => next.delete(material.id));
        return next;
      });
      fetchMaterials(); // Refresh list after deletion
    } catch (err) {
      setError(`Failed to delete material: ${err.message || "Unknown error"}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const DeleteConfirmationDialog = () => {
    if (!showDeleteConfirm || deleteTargets.length === 0) return null;

    const maxListItems = 8;
    const materialCount = deleteTargets.length;
    const displayedTargets = deleteTargets.slice(0, maxListItems);

    return (
      <div className="delete-confirmation-overlay">
        <div className="delete-confirmation">
          <h3>Confirm Deletion</h3>
          <p>
            Are you sure you want to delete {materialCount} material
            {materialCount > 1 ? "s" : ""}?
          </p>
          <ul className="delete-material-list">
            {displayedTargets.map((material) => (
              <li key={material.id}>{material.name}</li>
            ))}
          </ul>
          {materialCount > maxListItems && (
            <p className="delete-more">
              ...and {materialCount - maxListItems} more.
            </p>
          )}
          <p>
            <strong>This action cannot be undone.</strong>
          </p>
          <div className="delete-confirmation-buttons">
            <button
              onClick={handleCancelDelete}
              disabled={isDeleting}
              className="cancel-button"
            >
              Cancel
            </button>
            <button
              onClick={handleConfirmDelete}
              disabled={isDeleting}
              className="confirm-delete-button"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </button>
          </div>
        </div>
      </div>
    );
  };

  const handleBulkDeleteClick = () => {
    if (selectedMaterialIds.size === 0) return;
    const selectedTargets = materials.filter((material) =>
      selectedMaterialIds.has(material.id),
    );
    setDeleteTargets(selectedTargets);
    setShowDeleteConfirm(true);
  };

  const toggleMaterialSelection = (materialId) => {
    setSelectedMaterialIds((prev) => {
      const next = new Set(prev);
      if (next.has(materialId)) {
        next.delete(materialId);
      } else {
        next.add(materialId);
      }
      return next;
    });
  };

  const toggleSelectAll = () => {
    const filteredIds = filteredMaterials.map((material) => material.id);
    setSelectedMaterialIds((prev) => {
      const next = new Set(prev);
      const allSelected =
        filteredIds.length > 0 && filteredIds.every((id) => next.has(id));
      if (allSelected) {
        filteredIds.forEach((id) => next.delete(id));
      } else {
        filteredIds.forEach((id) => next.add(id));
      }
      return next;
    });
  };

  const handleStartInlineEdit = (material) => {
    setEditingCell({ materialId: material.id, field: "cost_per_sqft" });
    setEditValue(material.cost_per_sqft ?? "");
    setEditError("");
  };

  const handleCancelInlineEdit = () => {
    setEditingCell(null);
    setEditValue("");
    setEditError("");
  };

  const handleSaveInlineEdit = async (material, nextValue) => {
    const trimmedValue = String(nextValue).trim();
    const numericValue = Number(trimmedValue);
    if (trimmedValue === "" || Number.isNaN(numericValue) || numericValue < 0) {
      setEditError("Enter a non-negative number.");
      return;
    }

    const previousValue = material.cost_per_sqft;
    setMaterials((prev) =>
      prev.map((item) =>
        item.id === material.id
          ? { ...item, cost_per_sqft: numericValue }
          : item,
      ),
    );
    setEditingCell(null);
    setEditError("");
    setSavingMaterialId(material.id);

    try {
      const updatedMaterial = await updateMaterial(material.id, {
        cost_per_sqft: numericValue,
      });
      setMaterials((prev) =>
        prev.map((item) => (item.id === material.id ? updatedMaterial : item)),
      );
    } catch (err) {
      setMaterials((prev) =>
        prev.map((item) =>
          item.id === material.id
            ? { ...item, cost_per_sqft: previousValue }
            : item,
        ),
      );
      setError(`Failed to update cost: ${err.message || "Unknown error"}`);
    } finally {
      setSavingMaterialId(null);
    }
  };

  const handleInlineKeyDown = (event, material) => {
    if (event.key === "Enter") {
      event.preventDefault();
      handleSaveInlineEdit(material, editValue);
    }
    if (event.key === "Escape") {
      event.preventDefault();
      ignoreBlurRef.current = true;
      handleCancelInlineEdit();
    }
  };

  const handleInlineBlur = (material) => {
    if (ignoreBlurRef.current) {
      ignoreBlurRef.current = false;
      return;
    }
    handleSaveInlineEdit(material, editValue);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setMinPrice("");
    setMaxPrice("");
  };

  const allFilteredSelected =
    filteredMaterials.length > 0 &&
    filteredMaterials.every((material) => selectedMaterialIds.has(material.id));

  const focusedMaterial =
    materials.find((material) => material.id === focusedMaterialId) || null;

  const rail = (
    <div className="material-rail">
      <MaterialFilterBar
        searchTerm={searchTerm}
        minPrice={minPrice}
        maxPrice={maxPrice}
        onSearchChange={setSearchTerm}
        onMinPriceChange={setMinPrice}
        onMaxPriceChange={setMaxPrice}
        onClearFilters={handleClearFilters}
      />
      <div className="bulk-actions-bar">
        <span>{selectedMaterialIds.size} selected</span>
        <button
          type="button"
          onClick={handleBulkDeleteClick}
          disabled={selectedMaterialIds.size === 0 || isDeleting}
          className="bulk-delete-button"
        >
          Delete Selected
        </button>
      </div>
    </div>
  );

  const center = (
    <div className="material-admin-page">
      {showImport && (
        <div className="material-import-slot">
          {/* Same component, same preview → confirm steps; it just no longer
              sits permanently on top of the table. */}
          <MaterialImport onImportComplete={fetchMaterials} />
        </div>
      )}

      {isLoading && <p className="loading-state">Loading materials...</p>}
      {error && <p className="error-message">Error: {error}</p>}

      {!isLoading && (
        <>
          <div className="materials-table-scroll">
            <table className="materials-table">
              <thead>
                <tr>
                  <th className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={allFilteredSelected}
                      onChange={toggleSelectAll}
                      aria-label="Select all materials"
                    />
                  </th>
                  <th className="text-center">ID</th>
                  <th className="text-center">Name</th>
                  <th className="text-center">Class</th>
                  <th className="text-center">Cost per SqFt</th>
                  <th className="text-center">Sheet Length (in)</th>
                  <th className="text-center">Sheet Width (in)</th>
                  <th className="text-center">Cost per Sheet</th>
                  <th className="text-center">Price updated</th>
                  {/* Fixed width, single trigger — a monetary column must never
                    be squeezed by a growing action cluster. */}
                  <th className="actions-cell text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMaterials.map((material) => {
                  const isEditing =
                    editingCell?.materialId === material.id &&
                    editingCell.field === "cost_per_sqft";
                  const isSaving = savingMaterialId === material.id;
                  return (
                    <tr
                      key={material.id}
                      onClick={() => setFocusedMaterialId(material.id)}
                      className={`${
                        selectedMaterialIds.has(material.id)
                          ? "selected-row"
                          : ""
                      }${
                        focusedMaterialId === material.id ? " focused-row" : ""
                      }`.trim()}
                    >
                      <td className="checkbox-cell">
                        <input
                          type="checkbox"
                          checked={selectedMaterialIds.has(material.id)}
                          onChange={() => toggleMaterialSelection(material.id)}
                          aria-label={`Select ${material.name}`}
                        />
                      </td>
                      <td className="text-center">{material.id}</td>
                      <td className="text-center">{material.name}</td>
                      <td className="text-center">
                        {material.material_class || "—"}
                      </td>
                      <td
                        className={`editable-cell ${isEditing ? "editing" : ""}`}
                        onDoubleClick={() =>
                          !isSaving && handleStartInlineEdit(material)
                        }
                      >
                        {isEditing ? (
                          <div className="inline-edit">
                            <input
                              type="number"
                              step="0.0001"
                              min="0"
                              value={editValue}
                              onChange={(event) => {
                                if (editError) setEditError("");
                                setEditValue(event.target.value);
                              }}
                              onKeyDown={(event) =>
                                handleInlineKeyDown(event, material)
                              }
                              onBlur={() => handleInlineBlur(material)}
                              autoFocus
                              onFocus={(event) => event.target.select()}
                              className={editError ? "input-error" : ""}
                            />
                            {editError && (
                              <div className="inline-edit-error">
                                {editError}
                              </div>
                            )}
                          </div>
                        ) : (
                          <div className="inline-display">
                            <span>
                              {formatCurrency(material.cost_per_sqft)}
                            </span>
                            {isSaving && (
                              <span
                                className="inline-saving"
                                aria-label="Saving"
                              />
                            )}
                          </div>
                        )}
                      </td>
                      <td className="text-center">
                        {material.sheet_length_in || "—"}
                      </td>
                      <td className="text-center">
                        {material.sheet_width_in || "—"}
                      </td>
                      <td className="text-center">
                        {material.cost_per_sheet
                          ? formatCurrency(material.cost_per_sheet)
                          : "—"}
                      </td>
                      <td className="text-center">
                        {material.price_updated_at ? (
                          <span
                            className={
                              isPriceStale(material.price_updated_at)
                                ? "price-updated price-updated-stale"
                                : "price-updated"
                            }
                            title={
                              isPriceStale(material.price_updated_at)
                                ? "Price may be stale"
                                : undefined
                            }
                          >
                            {formatPriceAge(material.price_updated_at)}
                          </span>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className="actions-cell text-center">
                        <button
                          type="button"
                          onClick={(event) => {
                            event.stopPropagation();
                            setRowMenuMaterial(material);
                          }}
                          className="wb-plain action-icon-button row-menu-trigger"
                          title="Row actions"
                          aria-label={`Actions for ${material.name}`}
                        >
                          <MoreVertical size={16} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {materials.length === 0 && (
            <p className="empty-state">
              No materials found. Add materials manually or import from a CSV
              file.
            </p>
          )}
          {materials.length > 0 && filteredMaterials.length === 0 && (
            <p className="empty-state">
              No materials match the current filters.
            </p>
          )}

          {/* Display the delete confirmation dialog */}
          <DeleteConfirmationDialog />
        </>
      )}
    </div>
  );

  const inspector = focusedMaterial ? (
    <MaterialDetail
      material={focusedMaterial}
      onEdit={() => handleEditMaterial(focusedMaterial)}
      onDelete={() => handleDeleteClick(focusedMaterial)}
    />
  ) : null;

  return (
    <>
      <WorkbenchShell
        variant="admin"
        title="Materials"
        subtitle="Sheet goods and their current cost"
        actions={
          <>
            <button
              type="button"
              className={`wb-tbtn${showImport ? " wb-tbtn--on" : ""}`}
              onClick={() => setShowImport((open) => !open)}
            >
              Import / export
            </button>
            <button
              type="button"
              className="wb-tbtn wb-tbtn--solid"
              onClick={handleAddClick}
            >
              + Add material
            </button>
          </>
        }
        railHeader={{ label: "Filters" }}
        rail={rail}
        centerHeader={{ label: "Materials", count: filteredMaterials.length }}
        center={center}
        inspectorHeader={{ label: "Material" }}
        inspector={inspector}
        statusBar={
          <>
            <StatusCell label="Materials" value={String(materials.length)} />
            <StatusCell
              label="Showing"
              value={String(filteredMaterials.length)}
            />
            <StatusCell
              label="Selected"
              value={String(selectedMaterialIds.size)}
            />
          </>
        }
      />

      {/* Row actions live in a dialog so the row height never shifts. */}
      <Dialog
        open={Boolean(rowMenuMaterial)}
        onOpenChange={(open) => {
          if (!open) setRowMenuMaterial(null);
        }}
      >
        <DialogContent className="material-row-dialog">
          <DialogHeader>
            <DialogTitle>{rowMenuMaterial?.name}</DialogTitle>
            <DialogDescription>
              {formatCurrency(rowMenuMaterial?.cost_per_sqft)} per square foot
            </DialogDescription>
          </DialogHeader>
          <div className="material-row-dialog__actions">
            <button
              type="button"
              className="wb-plainbtn material-row-dialog__btn"
              onClick={() => {
                const target = rowMenuMaterial;
                setRowMenuMaterial(null);
                handleEditMaterial(target);
              }}
            >
              Edit material
            </button>
            <button
              type="button"
              className="wb-plainbtn material-row-dialog__btn material-row-dialog__btn--danger"
              onClick={() => {
                const target = rowMenuMaterial;
                setRowMenuMaterial(null);
                handleDeleteClick(target);
              }}
            >
              Delete material
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

/** The inspector pane: everything the table row could not spell out. */
function MaterialDetail({ material, onEdit, onDelete }) {
  const rows = [
    ["Class", material.material_class || "—"],
    ["Cost per sqft", formatCurrency(material.cost_per_sqft)],
    [
      "Cost per sheet",
      material.cost_per_sheet ? formatCurrency(material.cost_per_sheet) : "—",
    ],
    ["Sheet length", material.sheet_length_in || "—"],
    ["Sheet width", material.sheet_width_in || "—"],
    [
      "Price updated",
      material.price_updated_at
        ? formatPriceAge(material.price_updated_at)
        : "—",
    ],
  ];

  return (
    <div className="material-detail">
      <h3 className="material-detail__name">{material.name}</h3>
      <dl className="material-detail__grid">
        {rows.map(([label, value]) => (
          <div className="material-detail__row" key={label}>
            <dt>{label}</dt>
            <dd>{value}</dd>
          </div>
        ))}
      </dl>
      {material.price_updated_at && isPriceStale(material.price_updated_at) ? (
        <p className="material-detail__stale">
          This price has not been refreshed in over a year — check it against
          the current vendor sheet before quoting.
        </p>
      ) : null}
      <div className="material-detail__actions">
        <button type="button" className="wb-tbtn" onClick={onEdit}>
          Edit
        </button>
        <button
          type="button"
          className="wb-tbtn material-detail__delete"
          onClick={onDelete}
        >
          Delete
        </button>
      </div>
    </div>
  );
}

export default MaterialAdminPage;
